(() => {
  'use strict';
  if (Number(window.Shopify?.theme?.id) !== 186777764218) return;

  const query = `mutation AmerrayCodQuote($input: CartInput!, $country: CountryCode)
    @inContext(country: $country) {
    cartCreate(input: $input) {
      userErrors { message }
      warnings { message }
      cart {
        id totalQuantity
        cost { subtotalAmount { amount currencyCode } }
        discountAllocations { discountedAmount { amount } }
        lines(first: 250) {
          nodes {
            id quantity attributes { key value }
            cost { amountPerQuantity { amount } subtotalAmount { amount } totalAmount { amount } }
            discountAllocations { discountedAmount { amount } }
            merchandise { ... on ProductVariant {
              id title availableForSale requiresShipping sku weight weightUnit
              selectedOptions { name value }
              product { id title handle vendor isGiftCard }
              image { url altText width height }
            } }
          }
        }
      }
    }
  }`;
  const cents = money => Math.round(Number(money.amount) * 100);
  const numericId = gid => Number(gid.split('/').pop());

  // Adapt Shopify-calculated prices to the Ajax-cart shape Releasit consumes.
  function appCart(cart) {
    const grams = { GRAMS: 1, KILOGRAMS: 1000, OUNCES: 28.349523125, POUNDS: 453.59237 };
    const items = cart.lines.nodes.map(line => {
      const variant = line.merchandise;
      const product = variant.product;
      const id = numericId(variant.id);
      const price = cents(line.cost.amountPerQuantity);
      const original = cents(line.cost.subtotalAmount);
      const final = cents(line.cost.totalAmount);
      return {
        id, variant_id: id, product_id: numericId(product.id), key: line.id,
        quantity: line.quantity, title: product.title + ' - ' + variant.title,
        product_title: product.title, variant_title: variant.title,
        product_has_only_default_variant: variant.title === 'Default Title',
        handle: product.handle, url: '/products/' + product.handle + '?variant=' + id,
        vendor: product.vendor, sku: variant.sku, gift_card: product.isGiftCard,
        requires_shipping: variant.requiresShipping,
        grams: Math.round((variant.weight || 0) * (grams[variant.weightUnit] || 1)),
        image: variant.image?.url || null,
        featured_image: variant.image ? { url: variant.image.url, alt: variant.image.altText, width: variant.image.width, height: variant.image.height } : null,
        options_with_values: variant.selectedOptions,
        variant_options: variant.selectedOptions.map(option => option.value),
        properties: Object.fromEntries(line.attributes.map(attribute => [attribute.key, attribute.value])),
        price, original_price: price, discounted_price: Math.round(final / line.quantity),
        final_price: Math.round(final / line.quantity), line_price: final,
        original_line_price: original, final_line_price: final,
        total_discount: original - final, discounts: [],
        line_level_total_discount: original - final,
        line_level_discount_allocations: line.discountAllocations.map(discount => ({
          amount: cents(discount.discountedAmount), discount_application: { type: 'automatic' }
        }))
      };
    });
    const original = items.reduce((sum, item) => sum + item.original_line_price, 0);
    const cartDiscount = cart.discountAllocations.reduce((sum, discount) => sum + cents(discount.discountedAmount), 0);
    const total = items.reduce((sum, item) => sum + item.final_line_price, 0) - cartDiscount;
    return {
      token: cart.id.replace('gid://shopify/Cart/', ''), note: null, attributes: {},
      currency: cart.cost.subtotalAmount.currencyCode, items, item_count: cart.totalQuantity,
      original_total_price: original, total_price: total, items_subtotal_price: total,
      total_discount: original - total,
      total_weight: items.reduce((sum, item) => sum + item.grams * item.quantity, 0),
      requires_shipping: items.some(item => item.requires_shipping),
      cart_level_discount_applications: cartDiscount ? [{ type: 'automatic', total_allocated_amount: cartDiscount }] : []
    };
  }

  function quoteInput(key) {
    const form = document.querySelector('.product__info-container product-form form');
    const formData = form ? new FormData(form) : new FormData();
    const data = new URLSearchParams(key.addData || '');
    const properties = {};
    for (const [name, value] of [...formData.entries(), ...data.entries()]) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const selected = data.get('id') || formData.get('id');
    const source = key.atcVariants || [{ id: selected, quantity: Number(data.get('quantity') || formData.get('quantity') || 1) }];
    if (!source.length) throw new Error('No product selected');
    const lines = source.map(item => {
      const id = String(item.id);
      const quantity = Number(item.quantity);
      if (!/^\d+$/.test(id) || !Number.isInteger(quantity) || quantity < 1) throw new Error('Invalid product quantity');
      const lineProperties = { ...(id === String(selected) ? properties : {}), ...item.properties };
      const line = {
        merchandiseId: 'gid://shopify/ProductVariant/' + id,
        quantity,
        attributes: Object.entries(lineProperties).map(([key, value]) => ({ key, value: String(value) }))
      };
      const plan = item.selling_plan || (id === String(selected) && (data.get('selling_plan') || formData.get('selling_plan')));
      if (plan) line.sellingPlanId = 'gid://shopify/SellingPlan/' + plan;
      return line;
    });
    const country = Shopify.country || 'MA';
    return { input: { lines, buyerIdentity: { countryCode: country } }, country };
  }

  function install(app) {
    const loader = app?.productPage?.instantLoading;
    if (!loader || loader.amerrayIsolated || !app.s.form.isEmbedded || !app.s.form.hasEmbeddedV2) return;
    loader.amerrayIsolated = true;
    let retryAfter = 0;
    loader.loadCartAndSetKey = function(key, done) {
      if (loader.stopTimeout || Date.now() < retryAfter) { done(false); return; }
      const requested = JSON.parse(JSON.stringify(key));
      app.s.form.currentPage = 'loading';
      const button = document.querySelector('#_rsi-cod-form-embed button[type="submit"]');
      if (button) button.disabled = true;
      Promise.resolve().then(async () => {
        const features = JSON.parse(document.getElementById('shopify-features').textContent);
        if (!features.accessToken) throw new Error('Missing Storefront access token');
        const response = await fetch('/api/2026-04/graphql.json', {
          method: 'POST', credentials: 'omit', signal: AbortSignal.timeout(15000),
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': features.accessToken },
          body: JSON.stringify({ query, variables: quoteInput(requested) })
        });
        if (!response.ok) throw new Error('Quote request failed: ' + response.status);
        const result = await response.json();
        const created = result.data?.cartCreate;
        const errors = [...(result.errors || []), ...(created?.userErrors || []), ...(created?.warnings || [])];
        if (errors.length || !created?.cart?.lines.nodes.length) throw new Error(errors.map(error => error.message).join('; ') || 'Empty quote');
        if (loader.stopTimeout) { done(false); return; }
        loader.cacheKey = requested;
        if (!loader.areCacheKeysSame(loader.calculateCacheKey())) {
          loader.cacheKey = false;
          done(false);
          return;
        }
        loader.cart = appCart(created.cart);
        retryAfter = 0;
        done(true);
      }).catch(error => {
        retryAfter = Date.now() + 15000;
        console.error('[AMERRAY COD quote]', error.message);
        let message = document.getElementById('amr-cod-quote-error');
        if (!message) {
          message = document.createElement('p');
          message.id = 'amr-cod-quote-error';
          message.setAttribute('role', 'alert');
          document.querySelector('#_rsi-cod-form-modal-form')?.appendChild(message);
        }
        message.textContent = 'Le calcul du total est indisponible. Nouvelle tentative en cours.';
        done(false);
      });
    };

    // A COD order uses its own cart; finishing it must not empty the shopping cart.
    const originalClear = app.cartManager.clearCart;
    app.cartManager.clearCart = function(success, failure) {
      if (app.form.mode !== 'normalCart' && app.s.form.isEmbedded && document.getElementById('_rsi-cod-form-embed')) {
        if (typeof success === 'function') queueMicrotask(success);
        return;
      }
      return originalClear.call(this, success, failure);
    };
  }

  // This Releasit hook runs before the embedded form starts its first cart request.
  const previousInit = window._COD_FORM_OVERWRITE_INIT;
  window._COD_FORM_OVERWRITE_INIT = function(...args) {
    const proceed = typeof previousInit === 'function' ? previousInit.apply(this, args) : true;
    if (proceed) install(window._rsi);
    return proceed;
  };
  if (window._rsi?.s?.form) install(window._rsi);
})();

